import { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import {
    ArrowUpTrayIcon,
    DocumentPlusIcon,
    TrashIcon,
} from "@heroicons/react/24/outline";
import { classNames } from "utils";
import PlacingSlipFilePreview, {
    formatFileSize,
    getFileExtension,
} from "./placing-slip-file-preview";

type Props = {
    files: File[];
    onChange: (files: File[]) => void;
    disabled?: boolean;
    uploading?: boolean;
    label?: string;
};

const ACCEPT = {
    "application/pdf": [".pdf"],
    "application/msword": [".doc"],
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
        ".docx",
    ],
};

const PlacingSlipFilePicker = ({
    files,
    onChange,
    disabled = false,
    uploading = false,
    label = "Placing Slip",
}: Props) => {
    const isDisabled = disabled || uploading;
    const [loading, setLoading] = useState(false);

    const onDrop = useCallback(
        async (acceptedFiles: File[]) => {
            if (!acceptedFiles.length || isDisabled) return;
            setLoading(true);
            try {
                onChange(acceptedFiles.slice(0, 1));
            } finally {
                setLoading(false);
            }
        },
        [isDisabled, onChange],
    );

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        multiple: false,
        accept: ACCEPT,
        maxFiles: 1,
        disabled: isDisabled || loading,
    });

    const removeFile = () => {
        onChange([]);
    };

    const selectedFile = files[0];

    return (
        <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
                {label}
            </label>

            {selectedFile ? (
                <div className="relative overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
                    <div className="flex items-center gap-3 p-3">
                        <PlacingSlipFilePreview file={selectedFile} />
                        <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-medium text-gray-900">
                                {selectedFile.name}
                            </p>
                            <p className="mt-0.5 text-xs text-gray-500">
                                {getFileExtension(selectedFile.name)} ·{" "}
                                {formatFileSize(selectedFile.size)}
                            </p>
                            {!isDisabled && !loading && !uploading && (
                                <button
                                    type="button"
                                    {...getRootProps()}
                                    className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-primary-600 hover:underline">
                                    <input {...getInputProps()} />
                                    <DocumentPlusIcon className="h-3.5 w-3.5" />
                                    Replace file
                                </button>
                            )}
                        </div>
                        {!isDisabled && (
                            <button
                                type="button"
                                onClick={removeFile}
                                disabled={uploading}
                                className="inline-flex shrink-0 items-center gap-1 rounded-md border border-red-200 bg-white px-2.5 py-1.5 text-xs font-medium text-red-600 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50">
                                <TrashIcon className="h-3.5 w-3.5" />
                                Remove
                            </button>
                        )}
                    </div>
                    {(loading || uploading) && (
                        <div className="absolute inset-0 z-10 flex items-center justify-center gap-2 bg-primary-600/90 px-4">
                            <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                            <p className="text-xs font-medium text-white">
                                {uploading
                                    ? "Uploading placing slip..."
                                    : "Processing file..."}
                            </p>
                        </div>
                    )}
                </div>
            ) : (
                <div
                    {...getRootProps()}
                    className={classNames(
                        "relative flex min-h-[96px] cursor-pointer items-center gap-3 rounded-lg border-2 border-dashed px-4 py-3 transition",
                        isDragActive
                            ? "border-primary-400 bg-primary-50"
                            : "border-gray-300 bg-gray-50 hover:border-primary-300 hover:bg-white",
                        isDisabled ? "cursor-not-allowed opacity-50" : "",
                    )}>
                    <input {...getInputProps()} />
                    {(loading || uploading) && (
                        <div className="absolute inset-0 z-10 flex items-center justify-center gap-2 rounded-lg bg-primary-600/90">
                            <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                            <p className="text-xs font-medium text-white">
                                {uploading
                                    ? "Uploading placing slip..."
                                    : "Processing file..."}
                            </p>
                        </div>
                    )}
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white shadow-sm">
                        <ArrowUpTrayIcon className="h-5 w-5 text-primary-600" />
                    </div>
                    <div className="min-w-0 text-left">
                        <p className="text-sm font-medium text-gray-900">
                            {isDragActive
                                ? "Drop the placing slip here"
                                : "Upload placing slip"}
                        </p>
                        <p className="text-xs text-gray-500">
                            PDF, DOC, or DOCX
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PlacingSlipFilePicker;
